import {
	faCartShopping,
	faCircleQuestion,
	faClock,
	faEnvelope,
	faMagnifyingGlass,
	faPhone,
	faUserTie,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { resolveBrandPalette } from '@supply-mover-2-0/shared';
import { hexToRgb, useTheme, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import { useSalesRepInfo } from '../../hooks/useSalesRepInfo';
import { formatCurrency } from '../../lib/formatCurrency';
import { telHref } from '../../lib/supportContact';
import { useCartStore } from '../../stores/cartStore';
import { usePortalAuthStore } from '../../stores/portalAuthStore';
import { usePortalSiteStore } from '../../stores/portalSiteStore';
import { GetHelpModal } from '../GetHelpModal';
import { PortalNotificationBell } from '../PortalNotificationBell';
import { LanguageSwitcher } from './LanguageSwitcher';
import { UserMenu } from './UserMenu';

/**
 * Minimum query length before the live-debounced search fires. One- and
 * two-character queries are too noisy on a 24k-item catalog (every item
 * matches "a"), and the OpenSearch fan-out plus the on-screen flicker
 * isn't worth it. Pressing Enter still works for any length — that's
 * the user explicitly asking us to run the query as-is.
 */
const MIN_LIVE_SEARCH_CHARS = 3;

/**
 * Debounce window for the live search. Mobile gets a longer window
 * because thumb-typing has wider keystroke gaps and autocorrect
 * suggestions cause natural pauses we don't want to interpret as
 * "submit my query". Detected via `matchMedia('(pointer: coarse)')`,
 * which is the standard signal for touch-primary input — covers
 * phones, tablets, and Capacitor WebViews without a viewport-width
 * heuristic that misclassifies a narrow desktop window as mobile.
 */
const DEBOUNCE_MS_DESKTOP = 500;
const DEBOUNCE_MS_MOBILE = 750;

function isCoarsePointer(): boolean {
	if (typeof window === 'undefined' || window.matchMedia == null) {
		return false;
	}
	return window.matchMedia('(pointer: coarse)').matches;
}

/**
 * Portal header bar — persistent across all portal pages.
 *
 * Mobile (< md): only the search bar shows. Brand logo, language switcher,
 * cart icon, and user menu all collapse into the BottomNav + MenuDrawer
 * pattern (see `BottomNav.tsx` / `MenuDrawer.tsx`). This matches the
 * Amazon-app convention — search dominates the top, navigation lives at
 * the bottom — and reclaims the ~80px of horizontal space the logo would
 * otherwise eat from a small phone's search field. The drawer surfaces
 * brand identity prominently, so we don't lose tenant signal.
 *
 * Desktop (>= md): full layout — [Logo] [Search] [Cart] [Lang] [User].
 */
export function PortalHeader(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
	const totalQuantity = useCartStore((s) => s.totalQuantity);
	// PD-13692: show the running cart total next to the icon while browsing.
	const cartTotal = useCartStore((s) => s.cart?.total ?? null);
	const display = useDisplaySettings();
	// Start from the URL's `q` when the page loads on the catalog. Starting
	// empty made the live-search effect below treat the empty box as "clear
	// the search" and strip `q` on the first render, so opening or refreshing
	// a /catalog?q=... link (a shared search, a reload) lost the search term.
	const [searchQuery, setSearchQuery] = useState(() =>
		location.pathname === '/catalog'
			? (new URLSearchParams(location.search).get('q') ?? '')
			: '',
	);
	const lastNavigatedQueryRef = useRef<string | null>(null);

	// Brand identity pulled from the portal_site bootstrap — falls back to
	// a generic "Portal" label when bootstrap hasn't resolved yet or the
	// site has no customization configured.
	const brandDisplayName = usePortalSiteStore(
		(s) => s.site?.displayName ?? null,
	);
	const brandLogoUrl = usePortalSiteStore((s) => s.site?.logoUrl ?? null);
	const brandLogoUrlLight = usePortalSiteStore(
		(s) => s.site?.logoUrlLight ?? null,
	);

	const brandPrimary = usePortalSiteStore((s) => s.site?.primaryColor ?? null);
	// Header support slot (PD-14363 phone; PD-15235 made it configurable):
	// the portal site picks ONE thing to show — nothing, a help icon opening
	// the Get Help panel, a piece of support contact info, or the signed-in
	// account's salesperson. Default 'phone' preserves the original header.
	const headerSupportDisplay = usePortalSiteStore(
		(s) => s.site?.headerSupportDisplay ?? 'phone',
	);
	const supportContact = usePortalSiteStore(
		(s) => s.site?.supportContact ?? null,
	);
	// The brand's general sales line (PD-15552) — site-level and public, so
	// unlike the salesperson options below it needs no fetch and no auth gate.
	const salesContact = usePortalSiteStore((s) => s.site?.salesContact ?? null);
	// Fetched only when the header actually shows a salesperson option (and
	// a customer is signed in — the hook no-ops otherwise).
	const salesRep = useSalesRepInfo(
		headerSupportDisplay === 'sales-rep-name' ||
			headerSupportDisplay === 'sales-rep-email' ||
			headerSupportDisplay === 'sales-rep-phone',
	);
	const [isGetHelpOpen, setIsGetHelpOpen] = useState(false);

	// The header surface is `bg-brand-primary` (the brand primary in light
	// mode, the neutral navy in dark mode; see BrandThemeApplier). Pick the
	// wordmark that contrasts with THAT
	// surface — driven by the bg's luminance, not the page theme. A brand
	// whose primary is a dark/saturated color (e.g. teal) keeps a dark header
	// in light mode too, so its white-on-dark `logoUrl` must stay; only when
	// the header bg is itself light do we switch to the `logoUrlLight`
	// (dark-ink) variant. The previous `!isDark` check wrongly showed the
	// teal wordmark on a teal header.
	const { isDark } = useTheme();
	// PD-16045: the header is the brand primary in light mode and the neutral
	// navy in dark mode; resolve it the same way BrandThemeApplier does.
	const headerBg = resolveBrandPalette(
		{ primaryColor: brandPrimary },
		isDark ? 'dark' : 'light',
	).header;
	const headerBgIsLight = (() => {
		if (headerBg == null) {
			return false;
		} // unknown → assume dark → white logo
		const rgb = hexToRgb(headerBg);
		if (rgb == null) {
			return false;
		}
		// Perceptual luminance (same 0.5 split BrandThemeApplier uses to pick
		// near-black vs white header foreground).
		return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255 > 0.5;
	})();
	const effectiveLogoUrl =
		headerBgIsLight && brandLogoUrlLight != null && brandLogoUrlLight.length > 0
			? brandLogoUrlLight
			: brandLogoUrl;

	// Build a /catalog URL that mutates ONLY the `q` param and preserves
	// every other query string (category, subcategory, sort, filter
	// toggles, page). The earlier code path called `navigate('/catalog')`
	// or `navigate('/catalog?q=…')` which silently nuked any param the
	// catalog page was already filtering on — including the `?category=`
	// that the dashboard tile click had just pushed in. Compose a fresh
	// search string so search updates don't act as a global filter wipe.
	const buildCatalogUrlWithQ = (q: string): string => {
		const next = new URLSearchParams(location.search);
		if (q.length === 0) {
			next.delete('q');
		} else {
			next.set('q', q);
		}
		// Page is paired with the active filter set; resetting q usually
		// means "show me a fresh result list", and stale page=N from a
		// previous search would land on an out-of-bounds page.
		next.delete('page');
		const qs = next.toString();
		return qs.length === 0 ? '/catalog' : `/catalog?${qs}`;
	};

	const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		// Submitting a search closes the on-screen keyboard, as native apps
		// do. Without the blur the field kept focus after the keyboard's
		// Search key: the keyboard stayed up over the results, and the
		// BottomNav (which hides while a text field is focused, see
		// useIsKeyboardLikelyOpen) stayed hidden until the user tapped
		// somewhere else.
		const field = e.currentTarget.querySelector('input');
		if (field != null) {
			field.blur();
		}
		// Enter is the explicit-submit path — fires regardless of length
		// or page, so a one- or two-char query the user really wants
		// (NDC fragments, item-id prefixes) still works.
		if (searchQuery.trim().length > 0) {
			const trimmed = searchQuery.trim();
			lastNavigatedQueryRef.current = trimmed;
			navigate(buildCatalogUrlWithQ(trimmed));
		}
	};

	// Live debounced search. Fires only on the catalog page so typing in
	// the header from another route (orders, account) doesn't yank the
	// user away mid-task — Enter still navigates them across pages.
	// Empty input clears the query immediately (no debounce) so
	// Cmd/Ctrl-A + Delete instantly resets the catalog.
	useEffect(() => {
		const onCatalog = location.pathname === '/catalog';
		if (!onCatalog) {
			return;
		}

		const trimmed = searchQuery.trim();
		const currentUrlQ =
			new URLSearchParams(location.search).get('q')?.trim() ?? '';

		const fireNavigate = (q: string): void => {
			// Skip if the URL already matches our intended q. Compare
			// against the URL (not just our last pushed value) so a
			// fresh CatalogPage mount with an empty input doesn't push
			// over a `?category=...` URL that arrived from a dashboard
			// tile click. The `lastNavigatedQueryRef` alone wasn't
			// enough — on first mount it's effectively undefined, and
			// the empty-input path was firing replaceState that nuked
			// the entire query string.
			if (currentUrlQ === q) {
				lastNavigatedQueryRef.current = q;
				return;
			}
			if (lastNavigatedQueryRef.current === q) {
				return;
			}
			lastNavigatedQueryRef.current = q;
			navigate(buildCatalogUrlWithQ(q), { replace: true });
		};

		// Empty input: fire immediately to reset the catalog.
		if (trimmed.length === 0) {
			fireNavigate('');
			return;
		}

		// Below the live-search threshold: don't fire (Enter still does).
		if (trimmed.length < MIN_LIVE_SEARCH_CHARS) {
			return;
		}

		const wait = isCoarsePointer() ? DEBOUNCE_MS_MOBILE : DEBOUNCE_MS_DESKTOP;
		const timer = setTimeout(() => {
			fireNavigate(trimmed);
		}, wait);
		return () => {
			clearTimeout(timer);
		};
	}, [searchQuery, location.pathname, location.search, navigate]);

	// Keep the input in sync with the URL when the user navigates by
	// other means (e.g. category click pushes /catalog?category=...
	// without `q`). Without this the input would hold a stale value
	// from a previous search after a category nav.
	useEffect(() => {
		if (location.pathname !== '/catalog') {
			return;
		}
		const params = new URLSearchParams(location.search);
		const urlQ = params.get('q') ?? '';
		// Only adopt the URL query when the user isn't actively typing
		// — that's signalled by the input matching what we last pushed.
		if (
			lastNavigatedQueryRef.current != null &&
			lastNavigatedQueryRef.current !== urlQ
		) {
			setSearchQuery(urlQ);
			lastNavigatedQueryRef.current = urlQ;
		}
	}, [location.pathname, location.search]);

	return (
		// Header background uses the brand's primary color (--brand-primary
		// from BrandThemeApplier — flips between primaryColor and
		// primaryColorLight based on the active theme). The wordmark
		// (`effectiveLogoUrl`) is picked to match: white-on-dark wordmark
		// for the dark variant, dark-on-light wordmark for the light variant.
		// Tenants who only upload one logo get that logo in both modes.
		//
		// Foreground colors (text, icons, search input) all read from
		// `text-brand-primary-fg`, which BrandThemeApplier picks at runtime
		// based on the relative luminance of the active primary — near-black
		// on light primaries (e.g. a tenant's white light-mode primary),
		// white on dark primaries. This is the contract that lets a tenant
		// configure ANY primaryColorLight (white included) without losing
		// header readability. Alpha-modifier variants (`/10`, `/15`, `/60`,
		// `/85`) work because the utility is defined with `<alpha-value>`
		// in tailwind.config.js.
		<header
			className={`sticky top-0 z-30 bg-brand-primary text-brand-primary-fg border-b border-brand-primary-fg/10 px-4 sm:px-6 lg:px-8 ${
				display.canBrowseCatalog ? 'pb-1.5' : 'pb-0 md:pb-1.5'
			}`}
			style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}
		>
			{/* Header is `sticky top-0` AND owns the iOS safe-area-inset-
			    top inline (instead of receiving it as outer wrapper
			    padding). Two reasons:
			      1. The header's bg-brand-primary fills the entire status
			         bar / Dynamic Island region — no thin grey strip
			         between iOS chrome and the storefront chrome.
			      2. `sticky top-0` pins to the actual viewport top (not
			         the bottom of the wrapper's padding), so when the
			         user scrolls, the header glues to y=0 and its bg
			         continues to cover the safe-area zone above the
			         search-bar row. Without this, on Capacitor the
			         status-bar text + Dynamic Island sat over a partly-
			         transparent stripe that revealed the scrolling page
			         underneath.
			    `0.75rem` = 12px (`pt-3`) of in-chrome breathing room
			    BELOW the safe-area inset, before the search-bar row. */}
			{/* Full-width row — the header spans the viewport. The page content
			    columns vary in width (3xl–7xl), so no single max-width lines up
			    everywhere; instead the header's own `px-4 sm:px-6 lg:px-8` gutter
			    gives the logo (left) and the profile/cart cluster (right)
			    comfortable breathing room from the edge on wider screens.
			    PD-14703: row trimmed h-12 → h-10 (the search input is the tallest
			    child at ~36px; the extra height was dead band).
			    On mobile the ONLY row content is the search input, and that only
			    renders when the catalog is browsable — on account-only portals
			    (Capital Trading) the row would be an empty 40px band of brand
			    color under the status bar, so it collapses to zero on phones and
			    keeps its height from md up (logo + user cluster are md-only). */}
			<div
				className={`flex items-center gap-3 ${
					display.canBrowseCatalog
						? 'h-10'
						: 'h-0 overflow-hidden md:h-10 md:overflow-visible'
				}`}
			>
				{/* Left — brand identity. Desktop only: on mobile the search
				    bar takes the full width (Amazon-app pattern), and the
				    drawer surfaces the brand logo prominently. */}
				<button
					type="button"
					onClick={() => navigate('/')}
					className="hidden md:flex shrink-0 items-center gap-2 font-bold text-lg text-brand-primary-fg hover:opacity-80 transition-opacity"
					aria-label={brandDisplayName ?? t('Portal')}
				>
					{effectiveLogoUrl != null && effectiveLogoUrl !== '' ? (
						<img
							src={effectiveLogoUrl}
							alt={brandDisplayName ?? t('Portal')}
							className="h-8 object-contain"
						/>
					) : (
						<span>{t(brandDisplayName ?? 'Portal')}</span>
					)}
				</button>

				{/* Center — Search bar. Always rendered (including for
				    anonymous visitors) so the mobile header isn't an empty
				    band when not signed in. Anon visitors who use search
				    are sent to /catalog?q=... like everyone else; the
				    catalog page surfaces results the storefront permits
				    them to see, and otherwise prompts sign-in. Without
				    this, mobile guest mode shows ONLY a thin teal strip
				    with no affordances — wasted real estate, since the
				    Amazon-pattern strips logo + cart from the mobile
				    header (those live in the BottomNav / drawer). */}
				<form onSubmit={handleSearch} className="flex-1 min-w-0">
					{/* Search input renders only when the catalog is browsable;
					    the form stays as the flex-1 spacer either way so the logo
					    stays at the left and the profile/cart controls stay pinned
					    to the right edge (account-portal-only mode otherwise
					    collapsed them in toward the logo). */}
					{display.canBrowseCatalog && (
						<div className="relative max-w-2xl mx-auto">
							{/* Icon + field both key off --brand-primary-fg via the
						    `.search-on-brand` component class (index.css), which
						    matches the base-layer attribute-selector specificity
						    that used to force bg-brand-input + gray placeholder
						    onto this field (PD-14603). The icon can use the plain
						    utility — nothing outranks it on an svg. */}
							<FontAwesomeIcon
								icon={faMagnifyingGlass}
								className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-primary-fg/85 text-sm pointer-events-none"
							/>
							<input
								type="search"
								value={searchQuery}
								onChange={(e) => {
									setSearchQuery(e.target.value);
								}}
								// PD-16126: storefronts that show NDCs advertise
								// that the search takes item numbers and NDCs too.
								placeholder={
									display.showItemNdc
										? t('Search by name, item # or NDC...')
										: t('Search products...')
								}
								className="search-on-brand w-full pl-9 pr-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-portal-accent focus:border-transparent"
							/>
						</div>
					)}
				</form>

				{/* Right — Cart (auth only) + Language + User menu. Hidden
				    on mobile: the BottomNav owns Cart, and the MenuDrawer
				    owns user identity / language / theme / sign out. */}
				<div className="hidden md:flex items-center gap-1 shrink-0">
					{/* Header support slot (PD-14363 phone, made configurable by
					    PD-15235, sales options added by PD-15552) — desktop-only; on
					    mobile the MenuDrawer owns support info. The site-level contact
					    options (support and sales alike) are public by design; the
					    assigned-salesperson options and the help icon are signed-in-only
					    (the rep is per active account, and the Get Help panel
					    is an authenticated surface). */}
					{((): JSX.Element | null => {
						const slotClass =
							'hidden md:flex items-center gap-2 p-2 min-h-touch text-brand-primary-fg/85 hover:text-brand-primary-fg transition-colors';

						switch (headerSupportDisplay) {
							case 'phone':
								return supportContact?.phone != null ? (
									<a
										href={telHref(supportContact.phone)}
										className={slotClass}
										aria-label={t('Customer support phone')}
									>
										<FontAwesomeIcon icon={faPhone} className="text-sm" />
										<span className="text-sm font-semibold whitespace-nowrap">
											{t(supportContact.phone)}
										</span>
									</a>
								) : null;
							case 'email':
								return supportContact?.email != null ? (
									<a
										href={`mailto:${supportContact.email}`}
										className={slotClass}
										aria-label={t('Customer support email')}
									>
										<FontAwesomeIcon icon={faEnvelope} className="text-sm" />
										<span className="text-sm font-semibold whitespace-nowrap">
											{t(supportContact.email)}
										</span>
									</a>
								) : null;
							case 'business-hours':
								return supportContact?.businessHours != null ? (
									<span
										className="hidden md:flex items-center gap-2 p-2 text-brand-primary-fg/85"
										aria-label={t('Business hours')}
									>
										<FontAwesomeIcon icon={faClock} className="text-sm" />
										<span className="text-sm font-semibold whitespace-nowrap">
											{t(supportContact.businessHours)}
										</span>
									</span>
								) : null;
							case 'sales-rep-name':
								return isAuthenticated && salesRep != null ? (
									<span
										className="hidden md:flex items-center gap-2 p-2 text-brand-primary-fg/85"
										aria-label={t('Your salesperson')}
									>
										<FontAwesomeIcon icon={faUserTie} className="text-sm" />
										<span className="text-sm font-semibold whitespace-nowrap">
											{t(salesRep.name)}
										</span>
									</span>
								) : null;
							case 'sales-rep-email':
								return isAuthenticated && salesRep?.email != null ? (
									<a
										href={`mailto:${salesRep.email}`}
										className={slotClass}
										aria-label={t('Your salesperson email')}
									>
										<FontAwesomeIcon icon={faEnvelope} className="text-sm" />
										<span className="text-sm font-semibold whitespace-nowrap">
											{t(salesRep.email)}
										</span>
									</a>
								) : null;
							case 'sales-rep-phone':
								return isAuthenticated && salesRep?.phone != null ? (
									<a
										href={telHref(salesRep.phone)}
										className={slotClass}
										aria-label={t('Your salesperson phone')}
									>
										<FontAwesomeIcon icon={faPhone} className="text-sm" />
										<span className="text-sm font-semibold whitespace-nowrap">
											{t(salesRep.phone)}
										</span>
									</a>
								) : null;
							case 'sales-phone':
								return salesContact?.phone != null ? (
									<a
										href={telHref(salesContact.phone)}
										className={slotClass}
										aria-label={t('Sales phone')}
									>
										<FontAwesomeIcon icon={faPhone} className="text-sm" />
										<span className="text-sm font-semibold whitespace-nowrap">
											{t(salesContact.phone)}
										</span>
									</a>
								) : null;
							case 'sales-email':
								return salesContact?.email != null ? (
									<a
										href={`mailto:${salesContact.email}`}
										className={slotClass}
										aria-label={t('Sales email')}
									>
										<FontAwesomeIcon icon={faEnvelope} className="text-sm" />
										<span className="text-sm font-semibold whitespace-nowrap">
											{t(salesContact.email)}
										</span>
									</a>
								) : null;
							case 'help-icon':
								return isAuthenticated ? (
									<button
										type="button"
										onClick={() => {
											setIsGetHelpOpen(true);
										}}
										className={slotClass}
										aria-label={t('Get Help')}
									>
										<FontAwesomeIcon
											icon={faCircleQuestion}
											className="text-lg"
										/>
									</button>
								) : null;
							default:
								// 'none'
								return null;
						}
					})()}

					<GetHelpModal
						isOpen={isGetHelpOpen}
						onClose={() => {
							setIsGetHelpOpen(false);
						}}
					/>

					{/* Notifications bell (PD-16475) — signed-in customers only.
					    Desktop-only like the rest of this cluster; on phones the
					    menu drawer's Notifications row opens the same tray. */}
					{isAuthenticated && <PortalNotificationBell />}

					{/* Cart icon with badge — desktop-only. The mobile bottom
					    nav already has a Cart tab, so showing it in the
					    header on phones would be a redundant control. Gated on
					    canPurchase so browse-only / account-only storefronts
					    (no purchasing) don't show a dead cart affordance. */}
					{isAuthenticated && display.canPurchase && (
						<button
							type="button"
							onClick={() => navigate('/cart')}
							className="hidden md:flex relative p-2 min-h-touch items-center justify-center gap-2 text-brand-primary-fg/85 hover:text-brand-primary-fg transition-colors"
							aria-label={t('Cart')}
						>
							{/* The cart icon + count badge are ONE unit: the badge is an
							    in-flow flex child (negative margins overlap it onto the
							    icon's top-right corner) rather than absolutely positioned,
							    so its width contributes to layout. That keeps the badge's
							    right edge a CONSISTENT distance from the total no matter how
							    many digits the count has — the icon and badge move together
							    and the gap to the price never changes. */}
							<span className="relative inline-flex items-center">
								<FontAwesomeIcon icon={faCartShopping} className="text-xl" />
								{totalQuantity > 0 && (
									<span className="self-start -ml-2 -mt-2 bg-portal-cart text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 leading-none">
										{totalQuantity}
									</span>
								)}
							</span>
							{/* PD-13692: running cart total, gated on pricing visibility. */}
							{display.canSeePricing &&
								totalQuantity > 0 &&
								cartTotal != null && (
									<span className="text-sm font-semibold tabular-nums">
										{t(formatCurrency(cartTotal) ?? '')}
									</span>
								)}
						</button>
					)}

					{/* Language switcher — hidden unless multiple active languages */}
					<LanguageSwitcher />

					{/* User menu — shows sign in or user avatar with dropdown */}
					<UserMenu />
				</div>
			</div>
		</header>
	);
}
